// src/components/model-filter-tabs.tsx
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Layers, ChevronDown, Check } from "lucide-react";

type ModelTabItem =
  | string
  | {
      value: string;
      label: string;
    };

interface ModelFilterTabsProps {
  models: ModelTabItem[];
  selectedModel: string;
  onModelChange: (modelValue: string) => void;
  className?: string;

  combinedLabel?: string;

  includeCombined?: boolean;
}

function normalizeItem(item: ModelTabItem): { value: string; label: string } {
  if (typeof item === "string") return { value: item, label: item };
  return { value: item.value, label: item.label };
}

const MENU_WIDTH = 200;

export function ModelFilterTabs({
  models,
  selectedModel,
  onModelChange,
  className = "",
  combinedLabel = "Combined (All Models)",
  includeCombined = true,
}: ModelFilterTabsProps) {
  const normalized = models.map(normalizeItem);

  const allTabs: Array<{ value: string; label: string }> = includeCombined
    ? [{ value: "all", label: combinedLabel }, ...normalized]
    : normalized;

  const [open, setOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number } | null>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  // Escape closes the menu.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  const openMenu = () => {
    const r = triggerRef.current?.getBoundingClientRect();
    if (r) {
      // Anchor below the trigger, right-aligned to it; clamp into the viewport.
      const left = Math.max(8, Math.min(r.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - 8));
      setMenuPos({ top: r.bottom + 6, left });
    }
    setOpen(true);
  };

  const triggerLabel =
    selectedModel === "all"
      ? (includeCombined ? "All" : (allTabs.find(t => t.value === selectedModel)?.label ?? "All"))
      : (allTabs.find(t => t.value === selectedModel)?.label ?? selectedModel);

  return (
    <div
      className={className}
      onClick={(e) => e.stopPropagation()}
      style={{ display: "inline-block" }}
    >
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={(e) => { e.stopPropagation(); open ? setOpen(false) : openMenu(); }}
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 7,
          fontSize: "var(--text-xs)",
          fontWeight: 500,
          color: "var(--muted-foreground)",
          background: "var(--card)",
          border: "1px solid var(--border)",
          borderRadius: 8,
          padding: "6px 10px",
          whiteSpace: "nowrap",
          cursor: "pointer",
        }}
      >
        <Layers style={{ width: 13, height: 13, color: "var(--muted-foreground)" }} />
        <span>Model: {triggerLabel}</span>
        <ChevronDown style={{ width: 12, height: 12 }} />
      </button>

      {/* Menu is portaled to <body> so no card's overflow-hidden / transform can clip it. */}
      {open && menuPos && createPortal(
        <>
          <div
            onClick={(e) => { e.stopPropagation(); setOpen(false); }}
            style={{ position: "fixed", inset: 0, zIndex: 1000 }}
          />
          <div
            role="listbox"
            onClick={(e) => e.stopPropagation()}
            style={{
              position: "fixed",
              top: menuPos.top,
              left: menuPos.left,
              width: MENU_WIDTH,
              zIndex: 1001,
              background: "var(--card)",
              border: "1px solid var(--border)",
              borderRadius: 10,
              boxShadow: "var(--elevation-md)",
              padding: 6,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: ".05em",
                color: "var(--muted-foreground)",
                padding: "6px 10px 5px",
              }}
            >
              View by model
            </div>
            {allTabs.map((tab) => {
              const isSelected = tab.value === selectedModel;
              return (
                <button
                  key={tab.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={(e) => { e.stopPropagation(); onModelChange(tab.value); setOpen(false); }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 8,
                    textAlign: "left",
                    fontSize: 13,
                    fontWeight: isSelected ? 600 : 500,
                    color: isSelected ? "var(--ai-teal-text, #04938F)" : "var(--foreground)",
                    background: isSelected ? "var(--alpha-teal-10)" : "transparent",
                    border: "none",
                    borderRadius: 7,
                    padding: "8px 10px",
                    cursor: "pointer",
                  }}
                >
                  <span>{tab.label}</span>
                  {isSelected && <Check style={{ width: 14, height: 14, flexShrink: 0 }} />}
                </button>
              );
            })}
          </div>
        </>,
        document.body,
      )}
    </div>
  );
}
